import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { FilePdf, DownloadSimple } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { API, api, errMsg, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/layout/DashShell";
import { Section, Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("checking");
  useEffect(() => {
    api.post("/auth/verify-email", { token: sp.get("token") }).then(() => setState("ok")).catch((e) => setState(errMsg(e)));
  }, [sp]);
  return (
    <Section><div className="panel mx-auto max-w-lg p-8 text-center" data-testid="verify-email-result">
      {state === "checking" ? <Loader /> : state === "ok" ? <><h1 className="font-display text-3xl font-bold uppercase">Email confirmed</h1><p className="mt-2">You can now book classes.</p><Link to="/cities" className="btn-gold mt-4">Find a class</Link></> : <p className="text-[#C8382E]">{state}</p>}
    </div></Section>
  );
}

export const VerifyBanner = () => {
  const { user } = useAuth();
  if (!user || user.email_verified !== false) return null;
  const resend = async () => { await api.post("/auth/resend-verification", { origin_url: window.location.origin }); toast.success("We sent a new confirmation link"); };
  return <div className="mb-4 rounded-md border border-[#E0A21B] bg-[#E0A21B]/10 p-3 text-sm" data-testid="verify-banner">Please confirm your email address before booking. <button className="font-semibold underline" onClick={resend} data-testid="resend-verify-btn">Send the link again</button></div>;
};

export const TwoFactorCard = () => {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  if (!user || user.role === "student") return null;
  const start = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(errMsg(e)); } };
  const enable = async () => { try { await api.post("/auth/2fa/enable", { code }); toast.success("Two-step login is on"); setSetup(null); setUser({ ...user, totp_enabled: true }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel space-y-3 p-5" data-testid="twofa-card">
      <h3 className="font-display text-xl font-bold uppercase">Two-step login</h3>
      {user.totp_enabled ? <p className="text-sm font-semibold text-[#1E8E5A]" data-testid="twofa-on">On. You'll be asked for a code from your authenticator app at every sign-in.</p> : !setup ? (
        <><p className="text-sm text-[#475569]">Protect the staff account with a code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password).</p><button className="btn-navy" onClick={start} data-testid="twofa-setup-btn">Set up two-step login</button></>
      ) : (
        <div className="space-y-2 text-sm"><p>Scan this with your authenticator app, then enter the 6-digit code.</p><QRCodeSVG value={setup.otpauth_url} size={160} /><p className="font-mono-num text-xs" data-testid="twofa-secret">Key: {setup.secret}</p>
          <div className="flex gap-2"><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="twofa-code-input" /><button className="btn-gold" onClick={enable} data-testid="twofa-enable-btn">Turn on</button></div></div>
      )}
    </div>
  );
};

export const TicketPdf = ({ rid }) => <a href={`${API}/me/registrations/${rid}/ticket.pdf`} target="_blank" rel="noreferrer" className="btn-outline px-3 py-1.5 text-sm" data-testid={`ticket-pdf-${rid}`}><FilePdf /> PDF ticket</a>;

export const ReceiptExtras = ({ orderId }) => {
  const { data: co } = useFetch("/company");
  const { data: notes } = useFetch(`/me/orders/${orderId}/credit-notes`);
  return (
    <>
      {co && <div className="mb-3 text-sm" data-testid="receipt-company"><strong>{co.legal_name}</strong><br />{co.address}<br />GST/HST number: {co.gst_hst_number}</div>}
      {(notes || []).map((n) => (
        <div key={n.number} className="mt-3 rounded-md border border-[#C8382E]/30 p-3 text-sm" data-testid={`credit-note-${n.number}`}>
          <div className="font-semibold">Credit note {n.number} · {fmtDate(n.created_at)}</div>
          {n.taxes.map((t) => <div key={t.label} className="flex justify-between"><span>{t.label} reversed</span><span className="font-mono-num">−{money(t.amount_cents)}</span></div>)}
          <div className="flex justify-between font-semibold"><span>Total credited</span><span className="font-mono-num">−{money(n.total_cents)}</span></div>
        </div>
      ))}
    </>
  );
};

const CompanyCard = () => {
  const { data, reload } = useFetch("/company");
  const [f, setF] = useState(null);
  if (!data) return null;
  const v = f || data;
  const save = async () => { try { await api.put("/admin/company", v); toast.success("Saved"); setF(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel space-y-2 p-5 text-sm" data-testid="company-card">
      <h3 className="font-display text-xl font-bold uppercase">Company details (receipts and invoices)</h3>
      {[["legal_name", "Legal name"], ["address", "Address"], ["gst_hst_number", "GST/HST number"], ["support_email", "Support email"]].map(([k, l]) => (
        <label key={k} className="block">{l}<Input value={v[k] || ""} onChange={(e) => setF({ ...v, [k]: e.target.value })} data-testid={`company-${k}`} /></label>
      ))}
      <button className="btn-navy" onClick={save} data-testid="company-save-btn">Save</button>
    </div>
  );
};

const PreviewCard = () => {
  const { data, reload } = useFetch("/site");
  if (!data) return null;
  const run = async (fn) => { try { const r = await fn(); toast.success(r); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = (on) => run(async () => {
    await api.put("/admin/site", { preview_mode: on });
    window.dispatchEvent(new Event("site-changed"));
    return on ? "Preview mode on" : "Preview mode off. The site is in normal mode.";
  });
  return (
    <div className="panel space-y-3 border-2 border-[#E0A21B] p-5 text-sm" data-testid="preview-mode-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-xl font-bold uppercase">Launching soon (preview mode)</h3>
        <Switch checked={data.preview_mode} onCheckedChange={toggle} data-testid="preview-mode-switch" />
      </div>
      <p className="text-[#475569]">While on, every page shows a "Launching soon" banner and the Reset button is available. Classes can be published before a venue is set; when you add a venue to a class, every registered buyer and attendee is emailed the venue automatically.</p>
      <button className="btn-navy" onClick={() => run(async () => `${(await api.post("/admin/site/publish-preview")).data.published} class(es) published`)} data-testid="publish-preview-btn">
        Publish all upcoming classes
      </button>
      {data.preview_mode && <ResetBox />}
    </div>
  );
};

const ResetBox = () => {
  const [txt, setTxt] = useState("");
  const [res, setRes] = useState(null);
  const go = async () => {
    try { const { data } = await api.post("/admin/site/reset-test-data", { confirm: txt }); setRes(data); setTxt(""); toast.success(`Test data cleared: ${data.total} records removed`); setTimeout(() => window.location.reload(), 2500); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-md border border-[#C8382E]/40 bg-[#C8382E]/5 p-4" data-testid="reset-test-data-box">
      <div className="font-bold text-[#C8382E]">Reset test data</div>
      <p className="text-xs text-[#475569]">Deletes all bookings, orders, tickets, signatures, refunds, gift cards, student accounts, quiz and flight log entries, support tickets and every email sign-up (Notify me, seminar, newsletter). Keeps classes, prices, venues, legal pages, settings and staff logins. Cannot be undone. Locked once the site is live.</p>
      <div className="flex gap-2">
        <Input placeholder="Type RESET" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="reset-confirm-input" />
        <button className="btn-navy !bg-[#C8382E] disabled:opacity-40" disabled={txt !== "RESET"} onClick={go} data-testid="reset-test-data-btn">Reset</button>
      </div>
      {res && <p className="text-xs text-[#475569]" data-testid="reset-result">Removed {res.total} records{Object.keys(res.removed).length ? `: ${Object.entries(res.removed).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(", ")}` : ""}.</p>}
    </div>
  );
};

const ResultsTileCard = () => {
  const { data, reload } = useFetch("/admin/results-tile");
  const [f, setF] = useState(null);
  if (!data) return null;
  const v = f || data;
  const save = async (patch = {}) => { try { await api.put("/admin/results-tile", { ...v, ...patch }); toast.success("Saved"); setF(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel space-y-2 p-5 text-sm" data-testid="results-tile-card">
      <h3 className="font-display text-xl font-bold uppercase">Home page results tile (optional)</h3>
      <p className="text-[#475569]">Hidden until the owner enters a verified figure, the dates and how it was measured, then switches it on.</p>
      <label className="block">Figure<Input value={v.figure} onChange={(e) => setF({ ...v, figure: e.target.value })} data-testid="tile-figure" /></label>
      <label className="block">Label<Input value={v.label} onChange={(e) => setF({ ...v, label: e.target.value })} data-testid="tile-label" /></label>
      <label className="block">Dates covered<Input value={v.period} onChange={(e) => setF({ ...v, period: e.target.value })} data-testid="tile-period" /></label>
      <label className="block">How we measure<Textarea value={v.method} onChange={(e) => setF({ ...v, method: e.target.value })} data-testid="tile-method" /></label>
      <div className="flex items-center gap-3"><button className="btn-outline" onClick={() => save()} data-testid="tile-save-btn">Save</button>
        <label className="flex items-center gap-2 font-semibold"><Switch checked={!!data.enabled} onCheckedChange={(on) => save({ enabled: on })} data-testid="tile-enabled-switch" /> Show on home page</label></div>
    </div>
  );
};

const SalesReport = () => {
  const { data } = useFetch("/admin/reports/sales");
  if (!data) return null;
  const T = ({ rows }) => <table className="w-full text-sm"><tbody>{rows.map((r) => <tr key={r.name} className="border-t"><td className="py-1">{r.name}</td><td className="font-mono-num">{r.tickets} tickets</td><td className="font-mono-num">{money(r.net_sales_cents)}</td><td className="font-mono-num">tax {money(r.tax_cents)}</td></tr>)}</tbody></table>;
  return (
    <div className="panel space-y-4 p-5" data-testid="sales-report">
      <h3 className="font-display text-xl font-bold uppercase">Sales</h3>
      <div><div className="eyebrow">By event</div>{data.by_event.length ? <T rows={data.by_event} /> : <p className="text-sm text-[#475569]">No sales yet.</p>}</div>
      <div><div className="eyebrow">By city</div><T rows={data.by_city} /></div>
      <div><div className="eyebrow">Tax collected by province</div>{data.tax_by_province.map((p) => <div key={p.province} className="text-sm">{p.province}: {Object.entries(p.taxes).map(([k, c]) => `${k} ${money(c)}`).join(" · ")}</div>)}</div>
    </div>
  );
};

export function LaunchSettings() {
  const { user } = useAuth();
  return (
    <>
      <PageTitle title="Launch settings" sub="Company details, sales and tax, and the optional results tile." />
      <div className="grid gap-4 lg:grid-cols-2">
        <SalesReport />
        <div className="space-y-4">{user.role === "owner" && <PreviewCard />}{user.role === "owner" && <CompanyCard />}<TwoFactorCard />{user.role === "owner" && <ResultsTileCard />}</div>
      </div>
    </>
  );
}

export const AttendeeCsv = ({ eventId }) => <a href={`${API}/admin/events/${eventId}/attendees.csv`} className="btn-outline" data-testid="attendees-csv-btn"><DownloadSimple /> Export attendees (CSV)</a>;

export function HowWeMeasure() {
  const { data } = useFetch("/results-tile");
  return <Section><div className="mx-auto max-w-3xl whitespace-pre-line" data-testid="how-we-measure">{data?.enabled ? <><h1 className="font-display text-4xl font-bold uppercase">How we measure</h1><p className="mt-2 text-sm text-[#475569]">{data.period}</p><p className="mt-4">{data.method}</p></> : "No results are published yet."}</div></Section>;
}
